/** Código de cada tela, carregado sob demanda, e a pré-carga do menu.
 *
 * Cada tela vira um pedaço separado do bundle: a primeira abertura baixa só o
 * que a tela inicial usa, não as 19 de uma vez.
 *
 * A pré-carga roda quando o mouse passa sobre um item do menu (ou o dedo toca,
 * no celular). Entre o mouse chegar ao item e o clique há uns 200–300 ms —
 * quase uma ida e volta inteira à API em produção. Nesse intervalo já saem o
 * código da tela e os dados que ela pede ao abrir, com **as mesmas chaves** do
 * `useQuery` da página: quando ela monta, encontra o cache pronto. Dado ainda
 * fresco (staleTime de 5 min) não é buscado de novo.
 */

import type { QueryClient } from '@tanstack/react-query';

import { ApiAuditoria } from './api/auditoria';
import { ApiPlataforma } from './api/plataforma';
import { Api } from './api/endpoints';
import { ApiArquivo } from './api/arquivo';
import { ApiFotos } from './api/fotos';
import { ApiNotas } from './api/notas';
import { ApiVisitas } from './api/visitas';

export const telas = {
  dashboard: () => import('./pages/Dashboard'),
  tesouraria: () => import('./pages/Tesouraria'),
  irmaos: () => import('./pages/Irmaos'),
  presencas: () => import('./pages/Presencas'),
  projecao: () => import('./pages/ProjecaoQuadro'),
  relatorios: () => import('./pages/Relatorios'),
  chancelaria: () => import('./pages/Chancelaria'),
  calendario: () => import('./pages/Calendario'),
  documentos: () => import('./pages/Documentos'),
  fotos: () => import('./pages/Fotos'),
  notas: () => import('./pages/NotasFiscais'),
  financeiro: () => import('./pages/Financeiro'),
  contas: () => import('./pages/ContasBancarias'),
  planoContas: () => import('./pages/PlanoContas'),
  fornecedores: () => import('./pages/Fornecedores'),
  orcamento: () => import('./pages/Orcamento'),
  eventos: () => import('./pages/Eventos'),
  trabalhos: () => import('./pages/Trabalhos'),
  arquivo: () => import('./pages/Arquivo'),
  auditoria: () => import('./pages/Auditoria'),
  plataforma: () => import('./pages/Plataforma'),
  cobranca: () => import('./pages/Cobranca'),
  comunicados: () => import('./pages/Comunicados'),
  loja: () => import('./pages/Loja'),
  datas: () => import('./pages/DatasImportantes'),
  visitas: () => import('./pages/Visitacoes'),
};

interface Precarga {
  tela: () => Promise<unknown>;
  dados?: (qc: QueryClient) => void;
}

const anoAtual = () => new Date().getFullYear();

const PRECARGAS: Record<string, Precarga> = {
  '/': { tela: telas.dashboard, dados: (qc) => qc.prefetchQuery({ queryKey: ['dashboard'], queryFn: Api.dashboard }) },
  '/tesouraria': {
    tela: telas.tesouraria,
    dados: (qc) =>
      qc.prefetchQuery({
        queryKey: ['tesouraria', 'saldo'],
        queryFn: () => Api.irmaosTesouraria({ ordenar_por: 'saldo' }),
      }),
  },
  '/irmaos': { tela: telas.irmaos, dados: (qc) => qc.prefetchQuery({ queryKey: ['membros'], queryFn: () => Api.membros() }) },
  '/presencas': {
    tela: telas.presencas,
    dados: (qc) =>
      qc.prefetchQuery({
        queryKey: ['presencas', false],
        queryFn: () => Api.painelPresencas({ incluir_inativos: false }),
      }),
  },
  '/graduacoes': {
    tela: telas.irmaos,
    dados: (qc) => qc.prefetchQuery({ queryKey: ['graduacoes'], queryFn: () => Api.graduacoes() }),
  },
  '/projecao': {
    tela: telas.projecao,
    dados: (qc) => qc.prefetchQuery({ queryKey: ['projecao-quadro'], queryFn: Api.projecaoQuadro }),
  },
  '/relatorios': {
    tela: telas.relatorios,
    dados: (qc) => qc.prefetchQuery({ queryKey: ['relatorio-quadro'], queryFn: Api.relatorioQuadro }),
  },
  '/chancelaria/sessoes': {
    tela: telas.chancelaria,
    dados: (qc) =>
      qc.prefetchQuery({
        queryKey: ['chancelaria-sessoes', undefined],
        queryFn: () => Api.sessoesChancelaria(undefined),
      }),
  },
  '/calendario': { tela: telas.calendario },
  '/chancelaria/datas': {
    tela: telas.datas,
    dados: (qc) =>
      qc.prefetchQuery({ queryKey: ['datas-importantes', 30], queryFn: () => Api.datasImportantes(30) }),
  },
  '/visitacoes': {
    tela: telas.visitas,
    dados: (qc) => qc.prefetchQuery({ queryKey: ['visitas', {}], queryFn: () => ApiVisitas.listar() }),
  },
  '/fotos': {
    tela: telas.fotos,
    dados: (qc) => qc.prefetchQuery({ queryKey: ['fotos', 'albuns'], queryFn: ApiFotos.albuns }),
  },
  '/notas-fiscais': {
    tela: telas.notas,
    // A mesma chave da tela aberta sem filtro: o cache vem pronto.
    dados: (qc) => {
      const filtros = { mes: '', status: '', tipo: '', vinculo: '' as const, busca: '' };
      return qc.prefetchQuery({ queryKey: ['notas', filtros], queryFn: () => ApiNotas.listar(filtros) });
    },
  },
  '/financeiro': {
    tela: telas.financeiro,
    dados: (qc) => {
      qc.prefetchQuery({ queryKey: ['financeiro-exercicios'], queryFn: Api.exerciciosFinanceiro });
      qc.prefetchQuery({ queryKey: ['fluxo', anoAtual()], queryFn: () => Api.fluxo(anoAtual()) });
    },
  },
  '/contas-bancarias': {
    tela: telas.contas,
    dados: (qc) =>
      qc.prefetchQuery({ queryKey: ['contas-bancarias', false], queryFn: () => Api.contasBancarias(true) }),
  },
  '/plano-contas': {
    tela: telas.planoContas,
    dados: (qc) => qc.prefetchQuery({ queryKey: ['plano-contas'], queryFn: () => Api.planoDeContas() }),
  },
  '/fornecedores': {
    tela: telas.fornecedores,
    dados: (qc) => {
      qc.prefetchQuery({
        queryKey: ['fornecedores', false],
        queryFn: () => Api.fornecedores({ apenas_ativos: true }),
      });
      qc.prefetchQuery({ queryKey: ['fornecedores-pendencias'], queryFn: Api.contrapartesPendentes });
    },
  },
  '/orcamento': {
    tela: telas.orcamento,
    dados: (qc) => qc.prefetchQuery({ queryKey: ['orcamento-exercicios'], queryFn: Api.exerciciosOrcamento }),
  },
  '/gob': { tela: telas.documentos, dados: (qc) => qc.prefetchQuery({ queryKey: ['gob'], queryFn: Api.gob }) },
  '/eventos': {
    tela: telas.eventos,
    dados: (qc) => qc.prefetchQuery({ queryKey: ['eventos', false], queryFn: () => Api.eventos(false) }),
  },
  '/arquivo': {
    tela: telas.arquivo,
    dados: (qc) => qc.prefetchQuery({ queryKey: ['arquivo', 'arvore'], queryFn: ApiArquivo.arvore }),
  },
  '/meus-documentos': {
    tela: telas.arquivo,
    dados: (qc) => qc.prefetchQuery({ queryKey: ['arquivo', 'meu'], queryFn: ApiArquivo.meu }),
  },
  '/trabalhos': {
    tela: telas.trabalhos,
    dados: (qc) => qc.prefetchQuery({ queryKey: ['trabalhos'], queryFn: () => Api.trabalhos() }),
  },
  '/auditoria': {
    tela: telas.auditoria,
    dados: (qc) => qc.prefetchQuery({ queryKey: ['auditoria-filtros'], queryFn: ApiAuditoria.filtros }),
  },
  '/plataforma/lojas': {
    tela: telas.plataforma,
    dados: (qc) => qc.prefetchQuery({ queryKey: ['plataforma-lojas'], queryFn: ApiPlataforma.lojas }),
  },
  '/plataforma/cobranca': { tela: telas.cobranca },
  '/atas': { tela: telas.documentos, dados: (qc) => qc.prefetchQuery({ queryKey: ['atas'], queryFn: () => Api.atas() }) },
  '/loja': {
    tela: telas.loja,
    dados: (qc) => qc.prefetchQuery({ queryKey: ['loja-atual'], queryFn: Api.lojaAtual }),
  },
};

/** Começa a buscar código e dados da tela. Seguro chamar repetidamente. */
export function precarregar(caminho: string, qc: QueryClient): void {
  const p = PRECARGAS[caminho];
  if (!p) return;
  // Falha aqui não importa: a tela tenta de novo ao abrir e mostra o erro.
  p.tela().catch(() => undefined);
  p.dados?.(qc);
}
